import { useState, type ReactNode } from "react";
import { type SelectChangeEvent } from "@mui/material";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";

import type { MediaItemDetails } from "../../types/mediaLibraryTypes";
import { fetchMediaItemDetails } from "~/MediaLibrary/api/fetchMediaItemDetails";
import { addToFavourites } from "~/MediaLibrary/api/addToFavourites";
import { userIsPublic } from "~/utils/roles";
import { getRedirectToRegisterPage } from "~/utils/url/urlUtils";
import { ImageWithFallback } from "../MediaLibraryItem/MediaLibraryItem";
import { MediaType } from "../../constants";
import { VideoPreview } from "./VideoPreview";
import { PdfPreview } from "./PdfPreview";
import { trackAssetInteraction, trackShareAsset } from "~/feature/Analytics/ga/mediaLibrary";

import classes from "./MediaPreview.module.less";

type PreviewRenderer = {
  render: (mediaItem: MediaItemDetails) => ReactNode;
  extension?: (mediaItem: MediaItemDetails) => string;
};

const getFileExtension = (mediaType: string) => {
  const extension = mediaType?.split("/")?.[1];
  return extension ? `.${extension}` : "";
};

const PreviewIcon = ({ children }: { children: ReactNode }) => <div className={classes.centerIcon}>{children}</div>;

export const PREVIEWS: Record<MediaType, PreviewRenderer> = {
  [MediaType.IMAGE]: {
    render: ({ previewUrl, mediaType }) => (
      <ImageWithFallback previewUrl={previewUrl} mediaType={mediaType} className={classes.previewImage} />
    ),
    extension: ({ mediaType }) => getFileExtension(mediaType),
  },
  [MediaType.VIDEO]: {
    render: (mediaItem) => <VideoPreview mediaItem={mediaItem} />,
    extension: ({ mediaType }) => getFileExtension(mediaType),
  },

  [MediaType.PDF]: {
    render: ({ documentPages }) => <PdfPreview pages={documentPages} />,
    extension: ({ mediaType }) => getFileExtension(mediaType),
  },

  [MediaType.ZIP]: {
    render: () => (
      <PreviewIcon>
        <FolderZipOutlinedIcon className={classes.icon} />
      </PreviewIcon>
    ),
    extension: () => ".zip",
  },

  [MediaType.SHEET]: {
    render: () => (
      <PreviewIcon>
        <FeedOutlinedIcon className={classes.icon} />
      </PreviewIcon>
    ),
    extension: () => ".xlsx",
  },

  [MediaType.WORD]: {
    render: () => (
      <PreviewIcon>
        <ArticleOutlinedIcon className={classes.icon} />
      </PreviewIcon>
    ),
    extension: () => ".docx",
  },

  [MediaType.PPT]: {
    render: () => (
      <PreviewIcon>
        <BrokenImageOutlinedIcon className={classes.icon} />
      </PreviewIcon>
    ),
    extension: () => ".docx",
  },

  [MediaType.UNKNOWN]: {
    render: () => (
      <PreviewIcon>
        <BrokenImageOutlinedIcon className={classes.icon} />
      </PreviewIcon>
    ),
    extension: ({ mediaType }) => mediaType,
  },
};

export const useMediaPreview = ({
  id,
  groupId,
  updateMediaItems,
  updateSelectedMediaItem,
}: {
  id: string;
  groupId: string;
  updateMediaItems: (id: string, updatedFav: boolean) => void;
  updateSelectedMediaItem: (mediaDetails: MediaItemDetails) => void;
}) => {
  const [mediaItemApiResponse, setMediaItemApiResponse] = useState<MediaItemDetails[]>([]);
  const [mediaItem, setMediaItem] = useState<MediaItemDetails>();
  const [languages, setLanguages] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");

  const fetchMediaItem = async () => {
    const response = await fetchMediaItemDetails(groupId);
    setMediaItemApiResponse(response);

    if (!response || response.length === 0) {
      return;
    }
    if (response.length === 1) {
      setMediaItem(response[0]);
    } else {
      const result = response.find((item) => item.id === id);
      if (result) {
        setMediaItem(result);
        if (result.language) {
          setSelectedLanguage(result.language);
        }
      }

      const languageArray = [...new Set(response.map((item) => item.language).filter(Boolean))];
      setLanguages(languageArray);
    }
  };

  const handleChangeLanguage = (e: SelectChangeEvent<string>) => {
    const selectedLang = e.target.value;
    const mediaItemDetails = mediaItemApiResponse.find((item) => item.language === selectedLang);
    setSelectedLanguage(selectedLang);
    if (mediaItemDetails) {
      setMediaItem(mediaItemDetails);
      updateSelectedMediaItem(mediaItemDetails);
    }
  };

  const handleClickFavourite = async () => {
    if (userIsPublic()) {
      window.location.replace(
        getRedirectToRegisterPage({
          targetPage: `${window.location.href}?favAssetId=${id}&isPreviewOpen=${true}`,
          openLogin: true,
        }),
      );
      return;
    }
    if (!mediaItem) {
      return;
    }
    const updateFavouriteValue = !mediaItem.isFavourite;
    const status = await addToFavourites({ assetID: id, addToFavourite: updateFavouriteValue });
    if (status === 200) {
      trackAssetInteraction(
        updateFavouriteValue ? "Bookmark Added" : "Bookmark Removed",
        id,
        mediaItem?.title,
        mediaItem?.language,
        mediaItem?.mediaType,
      );
      setMediaItem({ ...mediaItem, isFavourite: updateFavouriteValue });
      updateMediaItems(id, updateFavouriteValue);
    }
  };

  const trackOnShare = () => {
    trackAssetInteraction("Share Options Accessed", id, mediaItem?.title, mediaItem?.language, mediaItem?.mediaType);
  };

  const trackOnShareWith = (shareWith: string) => {
    trackShareAsset(shareWith, id, mediaItem?.title, mediaItem?.language, mediaItem?.mediaType);
  };

  return {
    PREVIEWS,
    mediaItem,
    languages,
    selectedLanguage,
    fetchMediaItem,
    handleChangeLanguage,
    handleClickFavourite,
    trackOnShare,
    trackOnShareWith,
  };
};
